import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/SafeAuthProvider";
import { useSettings } from "@/context/SettingsContext";
import { useDashboardAnalytics } from "@/hooks/useDashboardAnalytics";
import { useOnboarding } from "@/hooks/useOnboarding";
import InteractiveTutorial from "@/components/tutorial/InteractiveTutorial";
import ContextualHelp from "@/components/help/ContextualHelp";
import { cn } from "@/lib/utils";
import { Plus, BookOpen, Target, TrendingUp, Scroll, Sword, Shield, Crown } from "lucide-react";
import CharacterSheet from "@/components/character/CharacterSheet";
import { getRpgThemeClasses, getRpgTerminology } from "@/lib/utils";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode, enableRpgMode } = useSettings();
  const { totalEntries, unstuckRate, thisWeekEntries, loading } = useDashboardAnalytics();
  const { showOnboarding, completeOnboarding, skipOnboarding } = useOnboarding();

  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  const handleCreateEntry = () => {
    navigate("/journal");
  };

  const handleViewHistory = () => {
    navigate("/history");
  };

  // Helper function to convert name to Title Case
  const toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  };

  // Extract and format the user's name from email
  const getUserDisplayName = () => {
    if (!user?.email) return enableRpgMode ? 'adventurer' : 'there';
    const emailPrefix = user.email.split('@')[0];
    // Replace dots, underscores, and hyphens with spaces, then title case
    const formattedName = emailPrefix.replace(/[._-]/g, ' ');
    return toTitleCase(formattedName);
  };

  const getWelcomeText = () => {
    if (enableRpgMode) {
      return {
        title: `Hail and well met, ${getUserDisplayName()}!`,
        subtitle: "The realm awaits your next grand adventure!"
      };
    }
    return {
      title: `Welcome back, ${getUserDisplayName()}!`,
      subtitle: "Ready to get unstuck today?"
    };
  };

  const { title, subtitle } = getWelcomeText();

  // RPG-themed icons for stats
  const StatIcons = {
    total: enableRpgMode ? Scroll : Target,
    rate: enableRpgMode ? Crown : TrendingUp,
    week: enableRpgMode ? Sword : BookOpen
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      {/* Interactive Tutorial */}
      <InteractiveTutorial />

      {/* Welcome Header */}
      <header 
        className={cn(
          "text-center py-8",
          enableRpgMode ? "text-amber-900 dark:text-amber-100" : (darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]")
        )}
        role="banner"
      >
        <h1 className={cn(
          "text-3xl font-bold mb-2",
          enableRpgMode && "drop-shadow-md"
        )}>
          {title}
        </h1>
        <p className={cn(
          "text-lg",
          enableRpgMode ? "text-amber-700 dark:text-amber-300" : (darkMode ? "text-[#D896A4]" : "text-[#6127A0]"),
          enableRpgMode && "drop-shadow-sm"
        )}>
          {subtitle}
        </p>
      </header>

      {/* Contextual Help for New Users */}
      {totalEntries === 0 && (
        <ContextualHelp
          title="Getting Started"
          content="Welcome! Start your first journaling session by clicking 'Create New Entry' below. The dice will guide you through prompts designed to help you break through any challenge you're facing."
          type="guidance"
        />
      )}

      {/* Character Sheet - Show when RPG mode is enabled */}
      {enableRpgMode && (
        <section aria-labelledby="character-section">
          <h2 id="character-section" className="sr-only">Character Information</h2>
          <CharacterSheet />
        </section>
      )}

      {/* Quick Actions */}
      <section aria-labelledby="quick-actions">
        <h2 id="quick-actions" className="sr-only">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card 
            className={cn(
              "cursor-pointer hover:shadow-lg transition-all hover:scale-105 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2",
              enableRpgMode ? (
                "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600/40 text-amber-900 dark:text-amber-100 hover:shadow-amber-500/20"
              ) : (
                darkMode 
                  ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1] hover:bg-[#2d1a45]/90" 
                  : "bg-white/90 border-[#D896A4]/20 hover:bg-white"
              ),
              enableRpgMode && "rpg-border"
            )}
            role="button"
            tabIndex={0}
            onClick={handleCreateEntry}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCreateEntry();
              }
            }}
            aria-label={enableRpgMode ? "Begin a new quest" : "Create a new journal entry"}
            data-tutorial="create-entry-button"
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className={cn(
                "text-lg font-medium",
                enableRpgMode && "font-bold drop-shadow-sm"
              )}>
                {enableRpgMode ? getRpgTerminology("Create New Entry") : "Create New Entry"}
              </CardTitle>
              {enableRpgMode ? 
                <Scroll className="ml-auto h-5 w-5 text-amber-600 dark:text-amber-400" aria-hidden="true" /> : 
                <Plus className="ml-auto h-5 w-5" aria-hidden="true" />
              }
            </CardHeader>
            <CardContent>
              <p className={cn(
                "text-sm",
                enableRpgMode ? "text-amber-700 dark:text-amber-300" : (darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70")
              )}>
                {enableRpgMode 
                  ? "Cast the sacred dice and embark upon a new journaling adventure"
                  : "Roll the dice and start a new journaling session"
                }
              </p>
            </CardContent>
          </Card>

          <Card 
            className={cn(
              "cursor-pointer hover:shadow-lg transition-all hover:scale-105 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2",
              enableRpgMode ? (
                "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600/40 text-amber-900 dark:text-amber-100 hover:shadow-amber-500/20"
              ) : (
                darkMode 
                  ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1] hover:bg-[#2d1a45]/90" 
                  : "bg-white/90 border-[#D896A4]/20 hover:bg-white"
              ),
              enableRpgMode && "rpg-border"
            )}
            role="button"
            tabIndex={0}
            onClick={handleViewHistory}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleViewHistory();
              }
            }}
            aria-label={enableRpgMode ? "View your chronicle of adventures" : "View your journal history"}
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className={cn(
                "text-lg font-medium",
                enableRpgMode && "font-bold drop-shadow-sm"
              )}>
                {enableRpgMode ? getRpgTerminology("View History") : "View History"}
              </CardTitle>
              <BookOpen className={cn(
                "ml-auto h-5 w-5",
                enableRpgMode && "text-amber-600 dark:text-amber-400"
              )} aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <p className={cn(
                "text-sm",
                enableRpgMode ? "text-amber-700 dark:text-amber-300" : (darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70")
              )}>
                {enableRpgMode 
                  ? "Review your legendary tales and heroic progress"
                  : "Review your past journal entries and progress"
                }
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Stats Overview */}
      <section aria-labelledby="stats-overview">
        <h2 id="stats-overview" className="sr-only">Statistics Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={cn(
            enableRpgMode ? (
              "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600/40 text-amber-900 dark:text-amber-100"
            ) : (
              darkMode 
                ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
                : "bg-white/90 border-[#D896A4]/20"
            ),
            enableRpgMode && "rpg-border"
          )}>
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className={cn(
                "text-sm font-medium",
                enableRpgMode && "font-bold drop-shadow-sm"
              )}>
                {enableRpgMode ? getRpgTerminology("Total Entries") : "Total Entries"}
              </CardTitle>
              <StatIcons.total className={cn(
                "ml-auto h-4 w-4",
                enableRpgMode && "text-amber-600 dark:text-amber-400"
              )} aria-hidden="true" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-12 mb-1" />
              ) : (
                <div className={cn(
                  "text-2xl font-bold",
                  enableRpgMode && "text-amber-800 dark:text-amber-200 drop-shadow-sm"
                )} aria-label={`${totalEntries} total entries`}>{totalEntries}</div>
              )}
              <p className={cn(
                "text-xs",
                enableRpgMode ? "text-amber-600 dark:text-amber-400" : (darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70")
              )}>
                {totalEntries === 0 
                  ? (enableRpgMode ? "Begin your first adventure!" : "Start your first entry!") 
                  : (enableRpgMode ? "Legendary quests completed" : "Journal entries created")
                }
              </p>
            </CardContent>
          </Card>

          <Card className={cn(
            enableRpgMode ? (
              "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600/40 text-amber-900 dark:text-amber-100"
            ) : (
              darkMode 
                ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
                : "bg-white/90 border-[#D896A4]/20"
            ),
            enableRpgMode && "rpg-border"
          )}>
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className={cn(
                "text-sm font-medium",
                enableRpgMode && "font-bold drop-shadow-sm"
              )}>
                {enableRpgMode ? getRpgTerminology("Unstuck Rate") : "Unstuck Rate"}
              </CardTitle>
              <StatIcons.rate className={cn(
                "ml-auto h-4 w-4",
                enableRpgMode && "text-amber-600 dark:text-amber-400"
              )} aria-hidden="true" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-12 mb-1" />
              ) : (
                <div className={cn(
                  "text-2xl font-bold",
                  enableRpgMode && "text-amber-800 dark:text-amber-200 drop-shadow-sm"
                )}>{unstuckRate}</div>
              )}
              <p className={cn(
                "text-xs",
                enableRpgMode ? "text-amber-600 dark:text-amber-400" : (darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70")
              )}>
                {totalEntries === 0 
                  ? (enableRpgMode ? "Complete adventures to see mastery" : "Complete entries to see rate") 
                  : (enableRpgMode ? "Mastery achieved" : "Completed entries")
                }
              </p>
            </CardContent>
          </Card>

          <Card className={cn(
            enableRpgMode ? (
              "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600/40 text-amber-900 dark:text-amber-100"
            ) : (
              darkMode 
                ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
                : "bg-white/90 border-[#D896A4]/20"
            ),
            enableRpgMode && "rpg-border"
          )}>
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className={cn(
                "text-sm font-medium",
                enableRpgMode && "font-bold drop-shadow-sm"
              )}>
                {enableRpgMode ? getRpgTerminology("This Week") : "This Week"}
              </CardTitle>
              <StatIcons.week className={cn(
                "ml-auto h-4 w-4",
                enableRpgMode && "text-amber-600 dark:text-amber-400"
              )} aria-hidden="true" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-12 mb-1" />
              ) : (
                <div className={cn(
                  "text-2xl font-bold",
                  enableRpgMode && "text-amber-800 dark:text-amber-200 drop-shadow-sm"
                )}>{thisWeekEntries}</div>
              )}
              <p className={cn(
                "text-xs",
                enableRpgMode ? "text-amber-600 dark:text-amber-400" : (darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70")
              )}>
                {thisWeekEntries === 0 
                  ? (enableRpgMode ? "No adventures this week yet" : "No entries this week yet") 
                  : (enableRpgMode ? "Adventures this week" : "Entries this week")
                }
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Getting Started Section */}
      <section aria-labelledby="getting-started">
        <Card className={cn(
          enableRpgMode ? (
            "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600/40 text-amber-900 dark:text-amber-100"
          ) : (
            darkMode 
              ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
              : "bg-white/90 border-[#D896A4]/20"
          ),
          enableRpgMode && "rpg-border"
        )}>
          <CardHeader>
            <CardTitle 
              id="getting-started"
              className={cn(
                enableRpgMode && "font-bold drop-shadow-sm"
              )}
            >
              {enableRpgMode ? "Ready for Adventure?" : "Ready to Get Unstuck?"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className={cn(
              enableRpgMode ? "text-amber-700 dark:text-amber-300" : (darkMode ? "text-[#FBF3F1]/80" : "text-[#1E0C46]/80")
            )}>
              {enableRpgMode 
                ? "Wield the mystical dice-driven journaling system to vanquish mental barriers and discover newfound clarity."
                : "Use the dice-driven journaling system to break through mental blocks and find clarity."
              }
            </p>
            <Button 
              onClick={handleCreateEntry}
              className={cn(
                "w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                enableRpgMode ? "bg-amber-600 hover:bg-amber-700 text-white border border-amber-500/50 shadow-lg" : (darkMode ? "bg-[#6127A0] hover:bg-[#4d1f80]" : "bg-[#6127A0] hover:bg-[#4d1f80]")
              )}
              aria-label={enableRpgMode ? "Begin a new quest" : "Start creating a new journal entry"}
            >
              {enableRpgMode ? <Scroll className="mr-2 h-4 w-4" aria-hidden="true" /> : <Plus className="mr-2 h-4 w-4" aria-hidden="true" />}
              {enableRpgMode ? "Begin Quest" : "Start New Entry"}
            </Button>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default Dashboard;
